import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import clsx from "clsx";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import {
  buildHighlightedSnippet,
  meaningfulLength,
} from "../../helpers/highlightSnippet";
import { colorFromName } from "./chatColors";

// Mesma largura do drawer de busca do atendimento (components/MessageSearchDrawer).
const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  // Painel lateral com largura animada. Ele ocupa espaço de verdade no flex do
  // container, então a conversa encolhe em vez de ficar coberta.
  drawer: {
    flex: "none",
    width: 0,
    height: "100%",
    overflow: "hidden",
    backgroundColor: theme.palette.contactdrawer,
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // No celular o painel toma a tela inteira: 320px ao lado de uma conversa
    // estreita não deixaria espaço utilizável para nenhum dos dois.
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      top: 0,
      right: 0,
      zIndex: 4,
    },
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: 73,
    flex: "none",
    padding: theme.spacing(0, 1),
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  searchBox: {
    padding: theme.spacing(1),
    flex: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  emptyState: {
    padding: theme.spacing(3, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  resultItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  resultTopRow: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  resultSender: {
    fontWeight: 600,
    fontSize: "0.8rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultDate: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
  resultSnippet: {
    fontSize: "0.85rem",
    wordBreak: "break-word",
    marginTop: 2,
    color: theme.palette.text.primary,
  },
  highlight: {
    backgroundColor:
      theme.palette.type === "dark" ? "#ffd54f" : "rgba(255, 213, 79, 0.6)",
    // No escuro o texto do trecho é claro e sumiria sobre o realce.
    color: theme.palette.type === "dark" ? "#1c1c1c" : "inherit",
    borderRadius: 2,
    padding: "0 2px",
    fontWeight: 600,
  },
  loadMoreWrapper: {
    display: "flex",
    padding: theme.spacing(1),
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

const formatDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString();
};

const ChatSearchDrawer = ({
  open,
  onClose,
  chatId,
  currentUserId,
  onJumpToMessage,
}) => {
  const classes = useStyles();

  const [searchParam, setSearchParam] = useState("");
  const [messages, setMessages] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // Descarta resposta fora de ordem: com debounce curto, uma busca antiga pode
  // chegar depois de uma mais nova.
  const requestSeqRef = useRef(0);

  const trimmedSearch = searchParam.trim();
  const canSearch = meaningfulLength(trimmedSearch) >= 2;

  // Fechar ou trocar de conversa zera tudo.
  useEffect(() => {
    if (!open) {
      setSearchParam("");
      setMessages([]);
      setPageNumber(1);
      setHasMore(false);
      setLoading(false);
      setSearched(false);
      requestSeqRef.current += 1;
    }
  }, [open]);

  useEffect(() => {
    setSearchParam("");
    setMessages([]);
    setPageNumber(1);
    setHasMore(false);
    setSearched(false);
    requestSeqRef.current += 1;
  }, [chatId]);

  // Digitar reinicia a paginação.
  useEffect(() => {
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    if (!open || !chatId) return undefined;

    if (!canSearch) {
      setMessages([]);
      setHasMore(false);
      setSearched(false);
      setLoading(false);
      return undefined;
    }

    const seq = ++requestSeqRef.current;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/chats/${chatId}/messages/search`, {
          params: { searchParam: trimmedSearch, pageNumber },
        });

        if (seq !== requestSeqRef.current) return;

        const records = Array.isArray(data?.records) ? data.records : [];
        setMessages((prev) => (pageNumber === 1 ? records : [...prev, ...records]));
        setHasMore(Boolean(data?.hasMore));
        setSearched(true);
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setMessages([]);
        setHasMore(false);
        setSearched(true);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedSearch, pageNumber, open, chatId]);

  const senderLabel = (message) =>
    message?.senderId === currentUserId
      ? i18n.t("internalChat.reply.you")
      : message?.sender?.name || "";

  const renderResults = () => {
    if (loading && messages.length === 0) {
      return (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={22} />
        </div>
      );
    }

    if (!canSearch) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.t("internalChat.search.prompt")}
          </Typography>
        </div>
      );
    }

    if (searched && messages.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Typography variant="body2">
            {i18n.t("internalChat.search.noResults")}
          </Typography>
        </div>
      );
    }

    return (
      <List disablePadding>
        {messages.map((message) => {
          const name = senderLabel(message);
          return (
            <React.Fragment key={message.id}>
              <ListItem
                button
                className={classes.resultItem}
                onClick={() => onJumpToMessage?.(message.id)}
              >
                <div className={classes.resultTopRow}>
                  <span
                    className={classes.resultSender}
                    style={{ color: colorFromName(name) }}
                  >
                    {name}
                  </span>
                  <span className={classes.resultDate}>
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <ListItemText
                  disableTypography
                  primary={
                    <span className={classes.resultSnippet}>
                      {message.message
                        ? buildHighlightedSnippet(
                            message.message,
                            trimmedSearch,
                            classes.highlight
                          )
                        : (
                          <>
                            <AttachFileIcon style={{ fontSize: "1em", verticalAlign: "-0.15em", marginRight: 2 }} />
                            {message.mediaName || ""}
                          </>
                        )}
                    </span>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}

        {hasMore && (
          <div className={classes.loadMoreWrapper}>
            <Button
              fullWidth
              size="small"
              color="primary"
              disabled={loading}
              onClick={() => setPageNumber((prev) => prev + 1)}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : (
                i18n.t("internalChat.search.loadMore")
              )}
            </Button>
          </div>
        )}
      </List>
    );
  };

  return (
    <div className={clsx(classes.drawer, { [classes.drawerOpen]: open })}>
      <div className={classes.header}>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Typography>{i18n.t("internalChat.search.title")}</Typography>
      </div>

      <div className={classes.searchBox}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          autoFocus={open}
          placeholder={i18n.t("internalChat.search.placeholder")}
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className={classes.content}>{renderResults()}</div>
    </div>
  );
};

export default ChatSearchDrawer;
